// Marquee homepage hero with the floating SDLC ROI report mockup.
// The site nav lives in Shell.jsx (`SiteNav`) and is used across all pages.
// Vendor logos used in the trust strip, Recognize-your-stack cluster, and the
// Integrations page flow through `IntegrationLogo` in Integrations.jsx — see
// that file for how to add a new vendor's licensed SVG asset.
import { appUrl } from './app-origin.js';
import React from 'react';
import { t } from './i18n/useT.js';
import { localizedHref } from './i18n/bootstrap.js';

export function HeroBackground() {
  return <div className="sw-hero-bg sw-hero-bg--horizon" aria-hidden="true"></div>;
}

// Carousel of shipped connectors that have a licensed SVG in
// public/logos/landing-page/. Only tools with an available connector appear
// here; roadmap logos stay on the Integrations page. width/height are the
// SVG's aspect ratio at the carousel's 30px row, so the browser reserves the
// slot before the file arrives (no layout shift).
const STACK_LOGOS = [
  { slug: 'jira',         name: 'Jira',         width: 71,  height: 30 },
  { slug: 'github',       name: 'GitHub',       width: 109, height: 30 },
  { slug: 'azure-devops', name: 'Azure DevOps', width: 158, height: 30 },
  { slug: 'aws',          name: 'AWS',          width: 50,  height: 30 },
];

function StackLogoCarousel({ label = 'Recognize your stack' }) {
  const trackRef = React.useRef(null);
  const firstGroupRef = React.useRef(null);
  const stateRef = React.useRef({ down: false, startX: 0, scrollStart: 0 });

  React.useEffect(() => {
    const track = trackRef.current;
    const firstGroup = firstGroupRef.current;
    if (!track || !firstGroup) return;

    // Reduced motion: no auto-pan (the page's one perpetual animation);
    // drag/scroll interaction still works via native overflow.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Two identical, viewport-width groups create a real scroll range and a
    // seamless wrap point. This also avoids the old ping-pong track stalling
    // when the six logos happened to fit inside the viewport.
    const PX_PER_SEC = 30;
    let lastTs = 0;
    let pos = track.scrollLeft;
    let rafId = 0;
    const tick = (ts) => {
      if (!lastTs) {
        lastTs = ts;
        pos = track.scrollLeft;
      }
      const dt = Math.min(0.1, (ts - lastTs) / 1000);
      lastTs = ts;
      const loopWidth = firstGroup.offsetWidth;
      if (!stateRef.current.down && loopWidth > 0) {
        pos += PX_PER_SEC * dt;
        if (pos >= loopWidth) pos -= loopWidth;
        track.scrollLeft = pos;
      } else if (stateRef.current.down) {
        // Keep the JS position in sync with where the user drags to.
        pos = track.scrollLeft;
      }
      rafId = requestAnimationFrame(tick);
    };

    const onDown = (e) => {
      stateRef.current.down = true;
      stateRef.current.startX = e.clientX;
      stateRef.current.scrollStart = track.scrollLeft;
      track.setPointerCapture?.(e.pointerId);
      track.classList.add('is-grabbing');
    };
    const onMove = (e) => {
      if (!stateRef.current.down) return;
      const dx = e.clientX - stateRef.current.startX;
      track.scrollLeft = stateRef.current.scrollStart - dx;
    };
    const onUp = (e) => {
      if (!stateRef.current.down) return;
      stateRef.current.down = false;
      try { track.releasePointerCapture?.(e.pointerId); } catch (_) {}
      track.classList.remove('is-grabbing');
      const loopWidth = firstGroup.offsetWidth;
      pos = loopWidth > 0 ? track.scrollLeft % loopWidth : track.scrollLeft;
      track.scrollLeft = pos;
      lastTs = 0;
    };
    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);
    if (!reduced) rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      track.removeEventListener('pointerdown', onDown);
      track.removeEventListener('pointermove', onMove);
      track.removeEventListener('pointerup', onUp);
      track.removeEventListener('pointercancel', onUp);
    };
  }, []);

  return (
    <div className="sw-stack-carousel">
      <div className="sw-stack-carousel-label">{label}</div>
      <div className="sw-stack-carousel-viewport">
        <div
          ref={trackRef}
          className="sw-stack-carousel-track"
          aria-label={label}
          tabIndex={0}
        >
          {[false, true].map((duplicate) => (
            <div
              ref={duplicate ? undefined : firstGroupRef}
              key={duplicate ? 'duplicate' : 'primary'}
              className="sw-stack-carousel-group"
              aria-hidden={duplicate || undefined}
            >
              {STACK_LOGOS.map((logo) => (
                <div key={logo.slug} className="sw-stack-carousel-item" title={logo.name}>
                  <img
                    src={`/logos/landing-page/${logo.slug}.svg`}
                    alt={duplicate ? '' : logo.name}
                    width={logo.width}
                    height={logo.height}
                    draggable="false"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DeliveryTimeCurve — adapted from web/simulator/charts.jsx in ScopeWorth.DesignSystem (LandingCurve).
// Amdahl-style synthetic curve showing wall-clock weeks vs team size.
// Animates: path draws in on first viewport entry, the "OPTIMAL" point
// pulses, then the stat row fades in below.
// ---------------------------------------------------------------------------
function DeliveryTimeCurve({ height = 280 }) {
  const w = 460, h = height;
  // Amdahl-style serial+parallel time plus quadratic communication overhead.
  // Tuned so the optimum sits at n=4 and the right-hand climb dominates the
  // left — a 1-person team is ~1.7× a 4-person team, not 10×. Larger teams
  // pay coordination cost that grows with (n-1)².
  const pts = [];
  for (let n = 1; n <= 10; n++) {
    const wks = 10 * (0.35 + 0.65 / n) + 0.10 * (n - 1) * (n - 1);
    pts.push({ n, w: wks });
  }
  const maxW = Math.max(...pts.map(p => p.w));
  const minW = Math.min(...pts.map(p => p.w));
  const xFor = (n)  => 36 + ((n - 1) / 9) * (w - 60);
  // (h - 92) leaves ~24px of headroom between the optimum and the baseline
  // so the dropline reads as a real drop instead of a hair-line.
  const yFor = (wk) => 28 + (1 - (wk - minW) / (maxW - minW || 1)) * (h - 92);
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(p.n)},${yFor(p.w)}`).join(' ');
  const area = `${path} L${xFor(10)},${h - 40} L${xFor(1)},${h - 40} Z`;
  const optimalN = pts.reduce((acc, p) => (p.w < acc.w ? p : acc), pts[0]).n;
  const optX = xFor(optimalN);
  const optY = yFor(pts[optimalN - 1].w);

  const [ref, setRef] = React.useState(null);
  const [phase, setPhase] = React.useState('idle');           // 'idle' | 'drawing' | 'done'
  const [reduced, setReduced] = React.useState(false);
  const pathRef = React.useRef(null);
  const pathLen = React.useRef(1);

  React.useEffect(() => {
    const mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  React.useEffect(() => {
    if (!ref || phase !== 'idle') return undefined;
    if (reduced) { setPhase('done'); return undefined; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setPhase('drawing');
          io.disconnect();
        }
      });
    }, { threshold: 0.25 });
    io.observe(ref);
    return () => io.disconnect();
  }, [ref, phase, reduced]);

  React.useEffect(() => {
    if (phase !== 'drawing') return undefined;
    const el = pathRef.current;
    if (!el) { setPhase('done'); return undefined; }
    try { pathLen.current = el.getTotalLength?.() || 1; } catch { /* ignore */ }
    // Drive the dash-offset via CSS variables so the line draws in.
    const id = window.setTimeout(() => setPhase('done'), 1700);
    return () => window.clearTimeout(id);
  }, [phase]);

  const drawing = phase === 'drawing';
  const done    = phase === 'done';
  const dashStyle = reduced || done
    ? { strokeDasharray: 'none', strokeDashoffset: 0 }
    : { strokeDasharray: pathLen.current, strokeDashoffset: drawing ? 0 : pathLen.current };

  // Optimal dot traversal: after the curve finishes, the dot starts at the
  // first of the (up to 3) previous nodes and hops along to the optimal point.
  // `settled` gates the halo + label so they only appear once the dot arrives.
  const traversal = pts.slice(Math.max(0, optimalN - 4), optimalN);
  const [optStep, setOptStep] = React.useState(0);
  React.useEffect(() => {
    if (phase !== 'done') { setOptStep(0); return undefined; }
    if (reduced || traversal.length <= 1) { setOptStep(traversal.length - 1); return undefined; }
    setOptStep(0);
    const id = window.setInterval(() => {
      setOptStep((s) => {
        if (s + 1 >= traversal.length) { window.clearInterval(id); return s; }
        return s + 1;
      });
    }, 380);
    return () => window.clearInterval(id);
  }, [phase, reduced, traversal.length]);

  const curPt = traversal[Math.min(optStep, traversal.length - 1)] || pts[optimalN - 1];
  const offX  = xFor(curPt.n) - optX;
  const offY  = yFor(curPt.w) - optY;
  const settled = optStep >= traversal.length - 1;

  return (
    <div ref={setRef} className={`sw-hero-curve ${done ? 'is-done' : ''} ${reduced ? 'is-reduced' : ''}`}>
      <div className="sw-hero-curve-eyebrow">DELIVERY TIME VS TEAM SIZE · ILLUSTRATIVE</div>
      <h2 className="sw-hero-curve-title">More people, less return. Eventually, more time.</h2>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="xMidYMid meet"
        className="sw-hero-curve-svg"
        role="img"
        aria-label="Delivery time versus team size curve, illustrative"
      >
        <defs>
          <linearGradient id="sw-hero-curve-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4F46E5" stopOpacity="0.38"/>
            <stop offset="100%" stopColor="#4F46E5" stopOpacity="0"/>
          </linearGradient>
          <linearGradient id="sw-hero-curve-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#818CF8"/>
            <stop offset="55%" stopColor="#5EEAD4"/>
            <stop offset="100%" stopColor="#FCD34D"/>
          </linearGradient>
        </defs>

        {/* faint horizontal grid */}
        {[0.25, 0.5, 0.75].map(p => (
          <line key={p} x1={36} x2={w - 24} y1={28 + (h - 80) * p} y2={28 + (h - 80) * p}
            stroke="rgba(148,163,184,0.10)" strokeDasharray="3 6"/>
        ))}
        {/* baseline */}
        <line x1={36} y1={h - 40} x2={w - 24} y2={h - 40} stroke="rgba(148,163,184,0.22)"/>

        {/* area + curve — drawn on viewport entry */}
        <path d={area} fill="url(#sw-hero-curve-fill)"
          style={{ opacity: drawing || done ? 1 : 0, transition: 'opacity 700ms ease-out 300ms' }}/>
        <path ref={pathRef} d={path} fill="none" stroke="url(#sw-hero-curve-line)"
          strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
          style={{ ...dashStyle, transition: 'stroke-dashoffset 1700ms cubic-bezier(0.2, 0.8, 0.2, 1)' }}/>

        {/* per-n dots, staggered */}
        {pts.map((p, i) => (
          <circle key={p.n} cx={xFor(p.n)} cy={yFor(p.w)} r="3" fill="#CBD5E1"
            className={done ? 'sw-hero-curve-dot is-in' : 'sw-hero-curve-dot'}
            style={{ transitionDelay: `${i * 60}ms` }}/>
        ))}

        {/* Dropline from OPTIMAL to the X axis — closes the loop between the
            optimal point and the ~4 tick without adding chart junk. */}
        <line
          x1={optX} y1={optY} x2={optX} y2={h - 40}
          stroke="#5EEAD4" strokeOpacity="0.55"
          strokeWidth="1" strokeDasharray="2 4"
          className={settled ? 'sw-hero-curve-dropline is-in' : 'sw-hero-curve-dropline'}/>
        <text x={optX} y={h - 22} textAnchor="middle"
          fontSize="10" fontFamily="ui-monospace, monospace"
          fill="#5EEAD4" letterSpacing="0.10em" fontWeight="700"
          className={settled ? 'sw-hero-curve-droptick is-in' : 'sw-hero-curve-droptick'}>
          ~{optimalN}
        </text>

        {/* optimal halo + dot + label — the group's translate hops the dot
            along the previous 3 nodes before settling at the optimal point. */}
        <g className="sw-hero-curve-opt-group" style={{ transform: `translate(${offX}px, ${offY}px)` }}>
          <circle cx={optX} cy={optY} r="14" fill="rgba(20,184,166,0.20)"
            className={settled ? 'sw-hero-curve-halo is-in' : 'sw-hero-curve-halo'}/>
          <circle cx={optX} cy={optY} r="6" fill="#14B8A6"
            className={done ? 'sw-hero-curve-opt is-in' : 'sw-hero-curve-opt'}/>
          <text x={optX} y={optY - 22} textAnchor="middle"
            fontSize="10" fontFamily="ui-monospace, monospace" fill="#5EEAD4"
            letterSpacing="0.16em" fontWeight="700"
            className={settled ? 'sw-hero-curve-optlbl is-in' : 'sw-hero-curve-optlbl'}>
            OPTIMAL
          </text>
        </g>

        {/* axis labels — staggered fade-in from the axes */}
        <text x={36}      y={h - 16} fontSize="10" fontFamily="ui-monospace, monospace" fill="#64748B" letterSpacing="0.14em"
          className={`sw-hero-curve-axis sw-hero-curve-axis-x ${done ? 'is-in' : ''}`} style={{ transitionDelay: done ? '200ms' : '0ms' }}>FEWER PEOPLE</text>
        <text x={w - 24}  y={h - 16} textAnchor="end" fontSize="10" fontFamily="ui-monospace, monospace" fill="#64748B" letterSpacing="0.14em"
          className={`sw-hero-curve-axis sw-hero-curve-axis-x ${done ? 'is-in' : ''}`} style={{ transitionDelay: done ? '300ms' : '0ms' }}>MORE PEOPLE</text>
        <text x={20}      y={110}    fontSize="10" fontFamily="ui-monospace, monospace" fill="#64748B" letterSpacing="0.14em" transform={`rotate(-90 20 110)`}
          className={`sw-hero-curve-axis sw-hero-curve-axis-y ${done ? 'is-in' : ''}`} style={{ transitionDelay: done ? '400ms' : '0ms' }}>MORE WEEKS</text>
        <text x={20}      y={h - 50} fontSize="10" fontFamily="ui-monospace, monospace" fill="#64748B" letterSpacing="0.14em" transform={`rotate(-90 20 ${h - 50})`}
          className={`sw-hero-curve-axis sw-hero-curve-axis-y ${done ? 'is-in' : ''}`} style={{ transitionDelay: done ? '500ms' : '0ms' }}>FEWER WEEKS</text>
      </svg>

      <div className="sw-hero-curve-stats">
        {[
          { l: 'Modelled',   v: 'Amdahl + comms', tone: 'blue'  },
          { l: 'Inflection', v: '~4 people',      tone: 'teal'  },
          { l: 'Beyond',     v: 'Cost > speed',   tone: 'amber' },
        ].map((s, i) => (
          <div key={s.l} className={`sw-hero-curve-stat is-${s.tone} ${done ? 'is-in' : ''}`}
            style={{ transitionDelay: `${800 + i * 100}ms` }}>
            <div className="sw-hero-curve-stat-l">{s.l}</div>
            <div className="sw-hero-curve-stat-v">{s.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// HeroV4's backdrop: the same light-arc render, held still. Once the laptop is
// on screen there are two moving things above the fold competing for the eye,
// and the arc is the one nobody came for — it barely animates, so freezing it
// costs almost nothing visually and buys back 311 KB, a decoder, and sole
// ownership of motion for the product shot.
//
// A real <img>, not a CSS background: this is the page's LCP element on every
// viewport, and a background-image is invisible to the browser's preload scanner
// until the stylesheet has loaded and the element has been laid out. In the
// prerendered HTML the <img> is discovered on the first parse, and
// fetchpriority=high puts it ahead of the module graph. AVIF first (6 KB for the
// same 1920x1080 frame), JPEG fallback. Styled by `img.sw-hero-video--still`.
function HeroStillBg() {
  return (
    <div className="sw-hero-bg sw-hero-bg--video sw-hero-bg--still" aria-hidden="true">
      <picture className="sw-hero-still">
        <source type="image/avif" srcSet="/hero/hero-poster-1920.avif" />
        <img
          className="sw-hero-video sw-hero-video--still"
          src="/hero/hero-poster-1920.jpg"
          width="1920"
          height="1080"
          alt=""
          decoding="async"
          fetchpriority="high"
        />
      </picture>
      <div className="sw-hero-bg-scrim" />
    </div>
  );
}

// The product shot: the forensics loop playing inside a laptop.
//
// Four states show the poster instead of the video — not loaded yet, autoplay
// refused, prefers-reduced-motion, and small screens. The first two are what
// the `poster` attribute is for; the last two skip the <video> entirely so the
// ~2.4 MB (2560x1440 — the screen renders ~1880 device px wide on a retina
// desktop) is never fetched. A phone renders the screen under ~350px wide,
// where a dashboard screencast is unreadable anyway.
//
// Playback waits for the window `load` event rather than starting on mount, so
// the clip never competes with the hero's LCP.
const HERO_DEVICE_NARROW = '(max-width: 760px)';

function HeroDevice() {
  const videoRef = React.useRef(null);
  const [still, setStill] = React.useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      || window.matchMedia(HERO_DEVICE_NARROW).matches;
  });

  React.useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const narrow = window.matchMedia(HERO_DEVICE_NARROW);
    const onChange = () => setStill(motion.matches || narrow.matches);
    motion.addEventListener?.('change', onChange);
    narrow.addEventListener?.('change', onChange);
    return () => {
      motion.removeEventListener?.('change', onChange);
      narrow.removeEventListener?.('change', onChange);
    };
  }, []);

  React.useEffect(() => {
    if (still) return undefined;
    const start = () => {
      const v = videoRef.current;
      if (!v) return;
      // preload="none" means there is nothing buffered until we ask.
      v.load();
      v.play().catch(() => { /* autoplay refused — poster stays */ });
    };
    if (document.readyState === 'complete') { start(); return undefined; }
    window.addEventListener('load', start, { once: true });
    return () => window.removeEventListener('load', start);
  }, [still]);

  return (
    <div className="sw-hero-device">
      {still ? (
        // The still is what phones and reduced-motion users get instead of the
        // clip, so it is the LCP image there — and the 2560px master was being
        // decoded to fill a ~288px slot. `sizes` mirrors the screen aperture:
        // 78.569% of the device, which is itself min(94vw, 1240px).
        <img
          className="sw-hero-device-screen"
          src="/hero/hero-forensics-loop-poster-960.jpg"
          srcSet={
            '/hero/hero-forensics-loop-poster-480.jpg 480w, ' +
            '/hero/hero-forensics-loop-poster-640.jpg 640w, ' +
            '/hero/hero-forensics-loop-poster-960.jpg 960w, ' +
            '/hero/hero-forensics-loop-poster-1600.jpg 1600w, ' +
            '/hero/hero-forensics-loop-poster.jpg 2560w'
          }
          sizes="min(73.8vw, 974px)"
          width="2560"
          height="1440"
          alt={t('hero.deviceAlt')}
          loading="eager"
          decoding="async"
        />
      ) : (
        <video
          ref={videoRef}
          className="sw-hero-device-screen"
          muted
          loop
          playsInline
          preload="none"
          poster="/hero/hero-forensics-loop-poster.jpg"
          src="/hero/hero-forensics-loop.mp4"
          aria-label={t('hero.deviceAlt')}
          tabIndex={-1}
        />
      )}
      {/* `sizes` mirrors .sw-hero-device's width so a phone fetches the 800px
          chassis (11 KB) instead of the 2400px master (86 KB). */}
      <img
        className="sw-hero-device-frame"
        src="/hero/scopeworth-laptop-frame.webp"
        srcSet={
          '/hero/scopeworth-laptop-frame-800.webp 800w, ' +
          '/hero/scopeworth-laptop-frame-1200.webp 1200w, ' +
          '/hero/scopeworth-laptop-frame-1600.webp 1600w, ' +
          '/hero/scopeworth-laptop-frame.webp 2400w'
        }
        sizes="min(94vw, 1240px)"
        width="2400"
        height="1456"
        alt=""
        aria-hidden="true"
        decoding="async"
      />
      {/* The screen is itself the play control: the loop inside the laptop is
          the demo tour's own footage, so the obvious click lands where the eye
          already is. The hit area is the aperture rectangle — same geometry
          constants as .sw-hero-device-screen — sitting above the frame so the
          chassis is never covered. Idle it is invisible; the dim and the glyph
          are a hover/focus affordance (always shown on touch, which has no
          hover to reveal them). */}
      <button
        type="button"
        className="sw-hero-device-cta"
        onClick={() => window.openDemoTour?.('hero-device')}
        aria-label={t('hero.devicePlay')}
      >
        <span className="sw-hero-device-cta-veil" aria-hidden="true"/>
        <span className="sw-hero-device-play" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M7.5 5.34a1 1 0 0 1 1.5-.87l10.4 6.66a1 1 0 0 1 0 1.74L9 19.53a1 1 0 0 1-1.5-.87V5.34Z"/>
          </svg>
        </span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeroV4 — the product shot. Same Evidence Room backdrop (held still), copy
// centred, and the report artifact replaced by a laptop running the forensics
// investigation: hover the baseline, open the +21% adoption outcome's evidence,
// follow the cost to the $8,600 verdict. The loop is cut to return to its own
// first frame, so it cuts rather than fades.
// ---------------------------------------------------------------------------
export function HeroV4() {
  return (
    <>
      <header className="sw-hero sw-hero--v2 sw-hero--v4" id="top">
        <HeroStillBg/>
        <div className="sw-hero-inner sw-hero-inner--v4">
          <div className="sw-hero-copy sw-hero-copy--v4">
            <div className="sw-eyebrow sw-eyebrow-teal">
              <span className="sw-eyebrow-dot"></span>
              {t('hero.eyebrow')}
            </div>
            <h1 className="sw-hero-title">
              {t('hero.title')}{' '}
              <span className="sw-hero-title-accent">{t('hero.titleAccent')}</span>
            </h1>
            <div className="sw-hero-actions">
              <a className="sw-btn sw-btn-primary sw-btn-lg" href={appUrl('/sign-up')}>
                <span className="sw-btn-label">{t('hero.viewSample')}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
              </a>
              <a className="sw-btn sw-btn-ghost-dark sw-btn-lg" href={localizedHref('/contact?reason=demo')}>
                <span className="sw-btn-label">{t('hero.bookDemo')}</span>
              </a>
            </div>
          </div>
          <div className="sw-hero-devicewrap">
            <HeroDevice/>
          </div>
        </div>
      </header>
      <div className="sw-trust sw-trust--carousel">
        <div className="sw-trust-inner sw-trust-inner--carousel">
          <StackLogoCarousel label={t('hero.stackLabel')} />
        </div>
      </div>
    </>
  );
}

// Exported as `Hero` so existing imports work.
export const Hero = HeroV4;

// Debug handles for the design-iteration panel. Guarded because the
// build-time prerender imports this module in Node, where `window` is absent
// — same pattern as Forms.jsx and CookieConsent.jsx.
if (typeof window !== 'undefined') {
  window.Hero = Hero;
  window.DeliveryTimeCurve = DeliveryTimeCurve;
}
